/**
 * ShareCaptionBubble — the OPTIONAL caption / reply-text sub-bubble rendered
 * below the main row of a share bubble. Shared by BOTH share parents
 * (`ShareContentBubble` and `ShareUserBubble`), whose two call sites rendered
 * a byte-for-byte identical element before this extraction — same background,
 * same corner radii, same `linkifyText`, same `alignSelf`, and (by explicit
 * design) the same QA handle.
 *
 * Renders NOTHING when the message carries no caption: a caption-less share
 * stores `content: ''`, so the component returns `null` and no element exists
 * in the DOM at all. Keep it that way — see the QA-handle note below.
 *
 * Flutter source of truth:
 *  - Share-content caption: optional bubble below the row when
 *    `chatMessage.content` is non-empty (`chat_screen.dart:2239-2278` own /
 *    `:3367-3406` peer).
 *  - Share-user reply text: `chat_screen.dart:2043` (own) / `chat_screen.dart:3170`
 *    (peer) — the only place Flutter's `AppColors.ownMessageColor` appears
 *    inside a share-user bubble, whose MAIN peer-info bubble uses
 *    `peerMessageColor` instead (see `ShareUserIdentityBlock`).
 *
 * Theme tokens:
 *  - Background  → `theme.chat.ownMessage` on BOTH the own and the peer
 *    variant. Flutter explicitly uses `AppColors.ownMessageColor` on both
 *    sides (`chat_screen.dart:2245` own / `:3374` peer, for share-content).
 *  - Text colour → `theme.white` (Flutter `AppStyles.chatStyle`). Same mapping
 *    `TextBubble` uses.
 *
 * Responsive tokens (closest available on the project scale):
 *  - Gap above 4      → `responsive.spacing.xs` (= 4). Exact Flutter parity
 *    (`EdgeInsets.only(top: 4, …)` at `chat_screen.dart:2242` own / `:3371`
 *    peer — NOT `:2240` / `:3369`, which are the `content.isNullOrEmpty()`
 *    guards the branch's decrypt-gate comments cite).
 *  - Inner padding 10 → `responsive.spacing.sm` (= 8). Same mapping
 *    `TextBubble` uses (small UI deviation per `presentation.md`).
 *  - Font size 15     → `responsive.font.lg` (= 16). Same mapping `TextBubble`
 *    uses.
 *  - Border radii     → resolved by `getBubbleCornerRadii` and passed in as
 *    `corners` by the parent (see `MessageBubble.tsx`), so the caption
 *    collapses the same corner as the rest of the bubble run.
 *
 * URL linkification: same approach `TextBubble` uses — defer to `linkifyText`.
 * Anchor `onClick` already stops propagation inside that util, so tapping a
 * URL launches the URL instead of firing the parent bubble's outer tap
 * (content-details navigation for share-content, user-details for share-user).
 *
 * QA handle: `chat-bubble-share-caption-<documentId>` — deliberately the SAME
 * id on both parents, so one UI-test selector covers share-profile and
 * share-content captions alike. It is distinct from each parent's wrapper
 * handle (`chat-bubble-share-user-<id>` / `chat-bubble-share-photo-<id>` /
 * `chat-bubble-share-video-<id>`), which spans the shared payload too.
 * `data-qa-value` carries the rendered caption so a test can assert the
 * decrypted plaintext directly, without scraping the whole bubble (which for
 * share-user also contains the shared user's name and username). A
 * caption-less share stores `content: ''`, so NO element with this id exists
 * at all — the correct assertion there is a negative one. Note for UI-test
 * authors: `KeepAliveOutlet` keeps several chat routes mounted at once
 * (`RouteStack.tsx` only toggles `display`), so any query for this id must
 * scope with `[data-route-active="true"]`.
 */

import React from 'react';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { BubbleCornerRadii } from '@/domain/utils/chatUtils';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { linkifyText } from '@/presentation/utils/linkifyText';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface ShareCaptionBubbleProps {
  /** The share message whose `content` is the caption. */
  message: ChatMessageEntity;
  /**
   * Corner radii resolved once by the parent via
   * `ChatUtils.getBubbleCornerRadii` — the caption uses the SAME radii as the
   * bubble it hangs under.
   */
  corners: BubbleCornerRadii;
  /** Whether the message is owned by the current user (drives `alignSelf`). */
  isOwn: boolean;
}

export const ShareCaptionBubble: React.FC<ShareCaptionBubbleProps> = ({
  message,
  corners,
  isOwn,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const hasContent =
    typeof message.content === 'string' && message.content.length > 0;

  if (!hasContent) {
    return null;
  }

  return (
    <div
      {...qaAttr({
        id: `chat-bubble-share-caption-${message.documentId}`,
        value: message.content ?? '',
      })}
      style={{
        marginTop: responsive.spacing.xs,
        backgroundColor: theme.chat.ownMessage,
        borderTopLeftRadius: corners.topLeft,
        borderTopRightRadius: corners.topRight,
        borderBottomLeftRadius: corners.bottomLeft,
        borderBottomRightRadius: corners.bottomRight,
        paddingTop: responsive.spacing.sm,
        paddingBottom: responsive.spacing.sm,
        paddingLeft: responsive.spacing.sm,
        paddingRight: responsive.spacing.sm,
        color: theme.white,
        fontSize: responsive.font.lg,
        fontWeight: responsive.fontWeight.regular,
        textAlign: 'left',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        // Align horizontally with the main row's edge — Flutter wraps the
        // caption inside the same outer column as the main row, with
        // `crossAxisAlignment.end` for own / `.start` for peer
        // (`chat_screen.dart:2137` / `:3265` for share-content; the share-user
        // column uses the same alignment as its own main row).
        alignSelf: isOwn ? 'flex-end' : 'flex-start',
        maxWidth: '100%',
      }}
    >
      {linkifyText(message.content ?? '')}
    </div>
  );
};
